import { Event } from '@livecontrol/scheduler/model';
import { DateTime } from 'luxon';
import type { ReactElement } from 'react';
import { useState } from 'react';
import styled, { css } from 'styled-components';
import type { FlattenSimpleInterpolation } from 'styled-components';
import { Alert, Tooltip, useAlert, useConfirm } from '../../components';
import { useDefcon, useStream } from '../event-details';
import type { LiveLiteEventDetails } from './use-live-lite-event';

/*
 * The Lifecycle / Danger Zone buttons of the Distribution tab, with the same mutations, gating and
 * confirmations. They are deliberately a copy rather than a shared component: the originals sit inside
 * the Web Player section of `event-details-v2/distribution-tab.tsx`, tangled with its redirect and
 * banner editors, and this page is meant not to change that one.
 *
 * What they reach when clicked (nothing runs on load):
 *  - Link Event to Stream: asset service, then the webplayer manifest.
 *  - Move to Past Event: Mux (passthrough data).
 *  - Force Start Event: the webplayer manifest.
 *  - The four Refresh / Browser Refresh / Force End buttons: the webplayer backend directly.
 */

type CmdVariant = 'danger' | 'default' | 'primary';

interface Command {
  label: string;
  tip: string;
  title: string;
  content: string;
  variant?: CmdVariant;
  disabled?: boolean;
  action: () => Promise<void>;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 15px 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 11px;
  background: rgba(17, 27, 51, 0.3);

  .label {
    font-weight: 600;
    font-size: 12px;
    color: rgba(176, 186, 209, 0.65);
  }

  .label.danger {
    display: flex;
    align-items: center;
    gap: 7px;
    color: rgb(255, 210, 122);
  }
`;

const Buttons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
  margin-top: 9px;
`;

const DangerNote = styled.p`
  font: 400 11.5px / 1.5 'Inter', sans-serif;
  color: rgba(176, 186, 209, 0.55);
  margin-top: 9px;
`;

const CmdButton = styled.button<{ $variant: CmdVariant }>`
  padding: 8px 13px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.15s;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  ${({ $variant }): FlattenSimpleInterpolation => {
    if ($variant === 'primary') {
      // Fill + text color come from the shared .cc-primary class.
      return css`
        border: none;
        box-shadow: rgba(78, 95, 253, 0.32) 0px 6px 16px;

        &:hover,
        &:focus {
          outline: none;
          opacity: 0.92;
        }
      `;
    }

    if ($variant === 'danger') {
      return css`
        background: rgba(255, 80, 95, 0.12);
        border: 1px solid rgba(255, 80, 95, 0.4);
        color: rgb(255, 138, 147);

        &:hover,
        &:focus {
          outline: none;
          background: rgba(255, 80, 95, 0.2);
        }
      `;
    }

    return css`
      background: transparent;
      border: 1px solid rgba(255, 255, 255, 0.12);
      color: rgba(213, 221, 238, 0.92);

      &:hover,
      &:focus {
        outline: none;
        background: rgba(255, 255, 255, 0.06);
      }
    `;
  }}
`;

const sleep = async (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const CommandButton = ({
  command,
  busy,
  disabled,
  onClick
}: {
  command: Command;
  busy: boolean;
  disabled: boolean;
  onClick: () => void;
}): ReactElement => (
  <Tooltip placement='bottom-left' content={command.tip}>
    <CmdButton
      type='button'
      $variant={command.variant ?? 'default'}
      className={command.variant === 'primary' ? 'cc-primary' : undefined}
      disabled={disabled}
      onClick={onClick}
    >
      {busy && <i className='fad fa-spinner-third fa-spin' style={{ marginRight: 6 }} />}
      {command.label}
    </CmdButton>
  </Tooltip>
);

export const EventCommands = ({ event }: { event: LiveLiteEventDetails }): ReactElement => {
  const confirm = useConfirm();
  const alert = useAlert();
  const [running, setRunning] = useState<string>();
  const [forceLink] = useStream('forceStream');
  const [prepareSignal] = useStream('streamSignal');
  const [startEvent] = useStream('startEvent');
  const [stopEvent] = useStream('stopEvent');
  const [refresh] = useDefcon('defconCacheRefresh');
  const [refreshSignal] = useDefcon('defconCacheRefreshAndSignal');
  const [muxSync] = useDefcon('defconNuclearRefresh');
  const [browserRefresh] = useDefcon('defconForceBrowserRefresh');
  const [forceEnd] = useDefcon('defconForceEndStream');

  const eventId = Number(event.eid);
  const locationId = event.location ?? '';
  const cid = String(event.client?.cid ?? '');

  // Same gating as the Distribution tab.
  const isLive = event.status === Event.Phase.Live.toString();
  const isUpcoming = !!(event.startTime && DateTime.fromISO(event.startTime) > DateTime.now());
  const controlDisabled = (!isUpcoming && !isLive) || !cid;

  const lifecycleCommands: Command[] = [
    {
      label: 'Link Event to Stream',
      tip: 'Link current inbound stream to this event.',
      title: 'Link event to stream',
      content: 'This links the current inbound stream to this event. Continue?',
      variant: 'primary',
      action: async (): Promise<void> => {
        await forceLink({ eventToLink: eventId });
      }
    },
    {
      label: 'Move to Past Event',
      tip: 'Move Event from Live to Past Events.',
      title: 'Stop event',
      content: 'Are you sure you want to stop the event?',
      disabled: !isLive,
      action: async (): Promise<void> => {
        await stopEvent({ location: locationId, event: eventId });
        location.reload();
      }
    },
    {
      label: 'Force Start Event',
      tip: 'Move Event from Upcoming to Live.',
      title: 'Start event',
      content: 'Are you sure you want to start the event?',
      disabled: !isUpcoming,
      action: async (): Promise<void> => {
        await prepareSignal({ event: eventId });
        await sleep(30_000);
        await startEvent({ location: locationId, event: eventId });
        location.reload();
      }
    },
    {
      label: 'Refresh',
      tip: 'Syncs the Web Player with the Asset Service.',
      title: 'Are your sure you want to refresh the stream?',
      content: 'This action will sync the Web Player with the Asset Service.',
      disabled: controlDisabled,
      action: async (): Promise<void> => {
        await refresh(cid);
      }
    },
    {
      label: 'Refresh + Send Signal',
      tip: 'Syncs Web Player with the Asset Service and sends signal to auto start.',
      title: 'Are your sure you want to refresh the stream and send auto start signal?',
      content:
        'This action will sync the Web Player with the Asset Service and send the Start Signal to the Web Player so that the video auto starts.',
      disabled: controlDisabled,
      action: async (): Promise<void> => {
        await refreshSignal(cid);
      }
    },
    {
      label: 'Refresh + Mux Sync',
      tip: 'Syncs Asset Service with Mux, then triggers "Refresh + Start Signal".',
      title: 'Are your sure you want to refresh the stream and trigger "Refresh + Start Signal"?',
      content:
        'This action will take a few seconds to complete as it needs to re-sync Mux’s database with ours, and then trigger the “Refresh + Start Signal” action.',
      disabled: controlDisabled,
      action: async (): Promise<void> => {
        await muxSync(cid);
      }
    }
  ];

  const dangerCommands: Command[] = [
    {
      label: 'Browser Refresh',
      tip: "Forces everyone's browser to refresh at the same time.",
      title: "Are your sure you want to force everyone's browser to refresh at the same time?",
      content:
        'This action will cause the browser of anyone watching our web player to refresh, as if someone had pressed the “Reload button” on their browser.',
      variant: 'danger',
      disabled: controlDisabled,
      action: async (): Promise<void> => {
        await browserRefresh(cid);
      }
    },
    {
      label: 'Force End',
      tip: 'Ends the stream for everyone.',
      title: 'Are your sure you want to end the stream for everyone?',
      content: 'This action will stop and end the live stream for everyone.',
      variant: 'danger',
      disabled: controlDisabled,
      action: async (): Promise<void> => {
        await forceEnd(cid);
      }
    }
  ];

  const runCommand = (command: Command): void => {
    void (async (): Promise<void> => {
      const confirmed = await confirm({
        title: command.title,
        message: command.content,
        tone: command.variant === 'danger' ? 'danger' : 'default'
      });

      if (!confirmed) {
        return;
      }

      setRunning(command.label);
      alert.clear();

      try {
        await command.action();
      } catch (error: unknown) {
        // The stream mutations reject with the backend's message; show it instead of failing silently.
        alert.show(error instanceof Error ? error.message : `${command.label} failed.`);
      }

      setRunning(undefined);
    })();
  };

  return (
    <Wrapper>
      {alert.message !== undefined && <Alert message={alert.message} onDismiss={alert.clear} />}

      <div>
        <div className='label'>Lifecycle commands</div>
        <Buttons>
          {lifecycleCommands.map((command) => (
            <CommandButton
              key={command.label}
              command={command}
              busy={running === command.label}
              disabled={running !== undefined || command.disabled === true}
              onClick={(): void => {
                runCommand(command);
              }}
            />
          ))}
        </Buttons>
      </div>

      <div>
        <div className='label danger'>
          <i className='fas fa-exclamation-triangle' /> Danger Zone
        </div>
        <Buttons>
          {dangerCommands.map((command) => (
            <CommandButton
              key={command.label}
              command={command}
              busy={running === command.label}
              disabled={running !== undefined || command.disabled === true}
              onClick={(): void => {
                runCommand(command);
              }}
            />
          ))}
        </Buttons>
        <DangerNote>
          Force End terminates the live stream for all viewers across every destination and cannot be
          resumed.
        </DangerNote>
      </div>
    </Wrapper>
  );
};
